import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ExternalLink } from 'lucide-react';

function H2({ children }) {
  return <h2 className="font-serif text-[34px] text-ink-800 mt-16 mb-5 tracking-tight">{children}</h2>;
}
function P({ children }) {
  return <p className="text-[16px] leading-[1.8] text-ink-700 mt-4">{children}</p>;
}
function Note({ children }) {
  return <div className="text-[14px] leading-[1.75] text-ink-500 mt-5 pl-5 border-l-2 border-copper-500/60 italic">{children}</div>;
}

export default function Methodology() {
  return (
    <div className="min-h-screen bg-cream-100 text-ink-700">
      <Header />
      <section className="bg-ink-800 text-cream-100">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 py-16">
          <div className="kicker text-copper-500 mb-4">Methodology</div>
          <h1 className="font-serif text-[56px] md:text-[64px] leading-[0.98] tracking-tight">
            How we <span className="italic font-light text-copper-500">calculate.</span>
          </h1>
        </div>
      </section>
      <main className="max-w-3xl mx-auto px-6 lg:px-12 py-16">
        <P>
          Spill the Hill publishes Canadian government spending as the public record reports it.
          We don't recalculate, re-categorise, or estimate. Every figure links to a source you can re-verify.
        </P>

        <H2>How totals are calculated</H2>
        <P>
          The headline is <strong>gross claimed</strong>: the sum of the Amount column for every row in the period,
          the same convention the House of Commons Board of Internal Economy uses when publishing the Members'
          Expenditures Report.
        </P>
        <P>
          Per-MP totals on the latest-quarter ranking filter to parliamentarians who served any part of the period.
          Staff travel charged to an MP's budget envelope rolls up into that MP's total because the envelope is theirs.
        </P>

        <H2>What “discretionary” means</H2>
        <P>
          <strong>Salaries</strong> (constituency-office staff) is administered by the House Administration
          following a published pay structure, not freely varied by the MP, and is excluded from any
          discretionary subtotal. It still appears in the gross headline because the House reports it that way.
        </P>

        <H2>Sources</H2>
        <P>The datasets on this site come straight from these public sources:</P>
        <ul className="mt-6 space-y-3 text-[15px] text-ink-700">
          {[
            ['MP expenses', 'House of Commons of Canada, Members\u2019 Expenditures Report (quarterly).', 'https://www.ourcommons.ca/proactivedisclosure/en/members'],
            ['Federal contracts', 'Open Government Canada, Proactive Disclosure of Contracts.', 'https://open.canada.ca/data/en/dataset/d8f85d91-7dec-4fd1-8055-483b77225d8b'],
            ['Political donations', 'Elections Canada, Political Financing Transparency Reports.', 'https://www.elections.ca/'],
            ['Immigration', 'Immigration, Refugees and Citizenship Canada and Statistics Canada.', 'https://www.canada.ca/en/immigration-refugees-citizenship.html'],
            ['Health transfer', 'Department of Finance Canada, Federal Transfers to provinces and territories.', 'https://www.canada.ca/en/department-finance/programs/federal-transfers.html'],
            ['Public sector', 'Statistics Canada, Public sector employment (Table 14-10-0026-01).', 'https://www150.statcan.gc.ca/t1/tbl1/en/tv.action?pid=1410002601'],
            ['Project cost overruns', 'Office of the Auditor General of Canada and provincial Auditors-General.', 'https://www.oag-bvg.gc.ca/'],
          ].map(([k, desc, href]) => (
            <li key={k} className="flex gap-3">
              <span className="text-copper-500 mt-1">◊</span>
              <span><strong className="text-ink-800">{k}</strong> — {desc} <a href={href} target="_blank" rel="noopener noreferrer" className="text-copper-600 hover:text-copper-500 inline-flex items-center gap-0.5">view <ExternalLink size={11} /></a></span>
            </li>
          ))}
        </ul>

        <H2>Inflation adjustment</H2>
        <P>
          The all-time leaderboard offers a “Today's dollars” toggle that converts historical amounts
          to the purchasing power of the most recent quarter with published CPI data. The conversion uses Statistics
          Canada's Consumer Price Index, monthly, not seasonally adjusted (Table 18-10-0004-01).
          The method is straightforward ratio deflation.
        </P>

        <H2>News feed</H2>
        <P>
          The news feed gathers Canadian coverage of government spending, integrity matters, and conduct that draws
          public scrutiny. It is a noticeboard, not a publisher. The headlines are the words of the
          outlet that published them, shown verbatim and attributed.
        </P>
        <Note>
          Sources are a fixed allowlist of Canadian outlets with an editorial-standards process: CBC, The Globe and Mail,
          Toronto Star, National Post, La Presse, The Hill Times, iPolitics, CTV News, Global News,
          Macleans, The Tyee, Radio-Canada.
        </Note>

        <H2>Licence and attribution</H2>
        <P>
          The underlying datasets are released under the <a className="text-copper-600 hover:text-copper-500" href="https://open.canada.ca/en/open-government-licence-canada" target="_blank" rel="noopener noreferrer">Open Government Licence — Canada</a>. Reuse freely, with attribution to the source agency.
        </P>
      </main>
      <Footer />
    </div>
  );
}
